import {
  createEmptyNodePromptSection,
  normalizeNodePromptDraft,
} from '@/etc/nodePromptDraft'

export const CLIP_PARTITION_VERSION = 1
export const MAX_CLIP_PARTITION_SEGMENTS = 4
export const MAX_CLIP_PARTITION_MARKERS =
  MAX_CLIP_PARTITION_SEGMENTS - 1
export const MAX_CLIP_PARTITION_SECONDS = 30

function finiteNumber(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function clampFrame(value, min, max) {
  return Math.max(min, Math.min(max, Math.round(finiteNumber(value, min))))
}

function normalizeFrameCount(frameCount) {
  return Math.max(1, Math.round(finiteNumber(frameCount, 1)))
}

function normalizeFps(fps) {
  return Math.max(1, finiteNumber(fps, 24))
}

function normalizeMarkers(markers, startFrame, endFrameExclusive) {
  const unique = new Set()
  const list = Array.isArray(markers) ? markers : []

  list.forEach((marker) => {
    const frame = Math.round(finiteNumber(marker, startFrame))
    if (frame <= startFrame || frame >= endFrameExclusive) return
    unique.add(frame)
  })

  return [...unique]
    .sort((a, b) => a - b)
    .slice(0, MAX_CLIP_PARTITION_MARKERS)
}

function sectionTextByIndex(sections, index) {
  const section = Array.isArray(sections) ? sections[index] : null
  return typeof section?.text === 'string' ? section.text : ''
}

export function buildClipPartitionSegments(range, fps = 24) {
  const startFrame = Math.max(0, Math.round(finiteNumber(range?.startFrame, 0)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.round(finiteNumber(range?.endFrameExclusive, startFrame + 1)),
  )
  const markers = normalizeMarkers(
    range?.partition?.markers,
    startFrame,
    endFrameExclusive,
  )
  const boundaries = [startFrame, ...markers, endFrameExclusive]
  const normalizedFps = normalizeFps(fps)

  return boundaries.slice(0, -1).map((segmentStartFrame, index) => {
    const segmentEndFrameExclusive = boundaries[index + 1]
    const frameCount = segmentEndFrameExclusive - segmentStartFrame
    return {
      id: index + 1,
      index,
      startFrame: segmentStartFrame,
      endFrameExclusive: segmentEndFrameExclusive,
      frameCount,
      duration: frameCount / normalizedFps,
    }
  })
}

export function buildClipPartitionPromptDraft(range, fps = 24) {
  const previousSections = range?.partition?.sections
  const sections = buildClipPartitionSegments(range, fps).map((segment, index) => ({
    ...createEmptyNodePromptSection(segment.id),
    text: sectionTextByIndex(previousSections, index),
    duration: segment.duration,
    cameraProfile: previousSections?.[index]?.cameraProfile || null,
    cameraProfilePresetId:
      previousSections?.[index]?.cameraProfilePresetId || null,
    cameraProfileSuppressed:
      previousSections?.[index]?.cameraProfileSuppressed === true,
  }))

  return normalizeNodePromptDraft({
    text: '',
    sections,
    selectedSectionId: sections[0]?.id ?? null,
  })
}

export function normalizeClipRange(
  range,
  frameCount,
  fps = 24,
) {
  const normalizedFrameCount = normalizeFrameCount(frameCount)
  const current = range && typeof range === 'object' ? range : {}
  const startFrame = clampFrame(
    current.startFrame,
    0,
    normalizedFrameCount - 1,
  )
  const endFrameExclusive = clampFrame(
    current.endFrameExclusive || normalizedFrameCount,
    startFrame + 1,
    normalizedFrameCount,
  )
  const next = {
    enabled: !!current.enabled,
    startFrame,
    endFrameExclusive,
  }

  if (!current.partition || typeof current.partition !== 'object') {
    return next
  }

  const markers = normalizeMarkers(
    current.partition.markers,
    startFrame,
    endFrameExclusive,
  )
  const partitionRange = {
    ...next,
    partition: {
      version: CLIP_PARTITION_VERSION,
      markers,
      sections: Array.isArray(current.partition.sections)
        ? current.partition.sections
        : [],
    },
  }
  const promptDraft = buildClipPartitionPromptDraft(partitionRange, fps)
  partitionRange.partition.sections = promptDraft.sections
  return partitionRange
}

export function ensureClipPartition(range, frameCount, fps = 24) {
  const normalized = normalizeClipRange(range, frameCount, fps)
  if (normalized.partition) return normalized

  return normalizeClipRange(
    {
      ...normalized,
      partition: {
        version: CLIP_PARTITION_VERSION,
        markers: [],
        sections: [],
      },
    },
    frameCount,
    fps,
  )
}

export function addClipPartitionMarker(
  range,
  frame,
  frameCount,
  fps = 24,
) {
  const partitioned = ensureClipPartition(range, frameCount, fps)
  if (
    partitioned.partition.markers.length >=
    MAX_CLIP_PARTITION_MARKERS
  ) {
    return partitioned
  }

  return normalizeClipRange(
    {
      ...partitioned,
      partition: {
        ...partitioned.partition,
        markers: [...partitioned.partition.markers, frame],
      },
    },
    frameCount,
    fps,
  )
}

export function moveClipPartitionMarker(
  range,
  markerIndex,
  frame,
  frameCount,
  fps = 24,
) {
  const partitioned = ensureClipPartition(range, frameCount, fps)
  const markers = [...partitioned.partition.markers]
  if (!Number.isInteger(markerIndex) || !markers[markerIndex]) {
    return partitioned
  }

  const min = markerIndex === 0
    ? partitioned.startFrame + 1
    : markers[markerIndex - 1] + 1
  const max = markerIndex === markers.length - 1
    ? partitioned.endFrameExclusive - 1
    : markers[markerIndex + 1] - 1
  markers[markerIndex] = clampFrame(frame, min, max)

  return normalizeClipRange(
    {
      ...partitioned,
      partition: {
        ...partitioned.partition,
        markers,
      },
    },
    frameCount,
    fps,
  )
}

export function removeClipPartitionMarker(
  range,
  markerIndex,
  frameCount,
  fps = 24,
) {
  const partitioned = ensureClipPartition(range, frameCount, fps)
  const markers = partitioned.partition.markers.filter(
    (_marker, index) => index !== markerIndex,
  )

  return normalizeClipRange(
    {
      ...partitioned,
      partition: {
        ...partitioned.partition,
        markers,
      },
    },
    frameCount,
    fps,
  )
}

export function clipPartitionDurationSeconds(range, fps = 24) {
  const startFrame = Math.max(0, Math.round(finiteNumber(range?.startFrame, 0)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.round(finiteNumber(range?.endFrameExclusive, startFrame + 1)),
  )
  return (endFrameExclusive - startFrame) / normalizeFps(fps)
}

export function sliceClipRange(
  range,
  sliceStartFrame,
  sliceEndFrameExclusive,
  outputFrameCount,
  fps = 24,
) {
  const start = Math.max(0, Math.round(finiteNumber(sliceStartFrame, 0)))
  const end = Math.max(
    start + 1,
    Math.round(finiteNumber(sliceEndFrameExclusive, start + 1)),
  )
  const nextFrameCount = normalizeFrameCount(
    outputFrameCount || end - start,
  )
  const currentStart = Math.max(
    start,
    Math.round(finiteNumber(range?.startFrame, start)),
  )
  const currentEnd = Math.min(
    end,
    Math.round(finiteNumber(range?.endFrameExclusive, end)),
  )

  if (currentEnd <= currentStart) {
    return normalizeClipRange(
      {
        enabled: false,
        startFrame: 0,
        endFrameExclusive: nextFrameCount,
      },
      nextFrameCount,
      fps,
    )
  }

  const next = {
    enabled: !!range?.enabled,
    startFrame: currentStart - start,
    endFrameExclusive: currentEnd - start,
  }
  if (range?.partition && typeof range.partition === 'object') {
    next.partition = {
      ...range.partition,
      markers: (range.partition.markers || [])
        .filter((marker) => marker > currentStart && marker < currentEnd)
        .map((marker) => marker - start),
    }
  }

  return normalizeClipRange(next, nextFrameCount, fps)
}

export function validateClipPartition(range, fps = 24) {
  const rawMarkers = Array.isArray(range?.partition?.markers)
    ? range.partition.markers
    : []
  const uniqueRawMarkers = new Set(
    rawMarkers.map((marker) => Math.round(finiteNumber(marker, -1))),
  )
  if (uniqueRawMarkers.size > MAX_CLIP_PARTITION_MARKERS) {
    return {
      valid: false,
      reason: `A clip plan can contain at most ${MAX_CLIP_PARTITION_SEGMENTS} shots.`,
      duration: clipPartitionDurationSeconds(range, fps),
      segments: buildClipPartitionSegments(range, fps),
    }
  }
  const segments = buildClipPartitionSegments(range, fps)
  const duration = clipPartitionDurationSeconds(range, fps)

  if (segments.length > MAX_CLIP_PARTITION_SEGMENTS) {
    return {
      valid: false,
      reason: `A clip plan can contain at most ${MAX_CLIP_PARTITION_SEGMENTS} shots.`,
      duration,
      segments,
    }
  }
  if (duration > MAX_CLIP_PARTITION_SECONDS + 1e-6) {
    return {
      valid: false,
      reason: `Clip plans can be at most ${MAX_CLIP_PARTITION_SECONDS} seconds long.`,
      duration,
      segments,
    }
  }

  return {
    valid: true,
    reason: '',
    duration,
    segments,
  }
}
